/**
 * Utils to manipulate simple dates like strings "2022-07-01" handling time zone related issues easily.
 */

// TypeScript branded type to avoid unintended mixing SimpleDate and string
export type SimpleDate = string & { readonly isSimpleDate: unique symbol};

// Check if something is a valid SimpleDate
export function isSimpleDate(d: any): d is SimpleDate {
	if (d && (typeof d === 'string')) {
		try {
			// We could also check the string format with a regex first
			const date = new Date(d);  // Check actual date
			return !isNaN(date.getDate());  // E.g., 2021-02-29 is invalid
		} catch (e) {
			return false;
		}
	} else {
		return false;
	}
}

// Convert a js date into yyyy-mm-dd without using toISOString
// (that move from local timezone to GMT and may change the day component)
export function jsDate2SimpleDate(d: Date) {
	return joinSimpleDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

// Today (with respect to user's timezone) in format yyyy-mm-dd
export function simplyToday() {
	return jsDate2SimpleDate(new Date());
}

// Split string like 2022-05-01 into numeric components [2022, 5, 1]
export function splitSimpleDate(d: SimpleDate): [number, number, number] {
	const p = d.split('-').map(v => Number(v));
	if (p.length !== 3) {
		throw new Error(`Invalid SimpleDate "${d}"`);
	}
	return [p[0], p[1], p[2]];
}

// Join numeric components (2022, 5, 1) into string like 2022-05-01
export function joinSimpleDate(y: number, m: number, d: number): SimpleDate {
	return `${y}-${m < 10 ? '0' : ''}${m}-${d < 10 ? '0' : ''}${d}` as SimpleDate;
}

// Add any number of months to a Simple date (even negative months)
export function addMonths(d: SimpleDate, months: number) {
	let [dy, dm, dd] = splitSimpleDate(d);
	dm = dm + months;
	while (dm < 1) {
		dy--;  // go back one year
		dm += 12;
	}
	while (dm > 12) {
		dy++;  // go ahead one year
		dm -= 12;
	}
	return joinSimpleDate(dy, dm, dd);
}

// Set day component to 01
export function firstDayOfMonth(date: SimpleDate): SimpleDate {
	return `${date.substring(0, 7)}-01` as SimpleDate;
}

// Get last day of a given month eg (2022, 12) gives 31 (support Feb on leap years)
function lastDayOfMonth(y: number, m: number): number {
	// We rely on js to get the last day of a given month
	const d = new Date(y, m, 0);  // day 0 of following month (months are 0 based in js)
	return d.getDate();
}

// Given d compute d - 1day (and works correctly across months)
export function previousDay(d: SimpleDate) {
	let [dy, dm, dd] = splitSimpleDate(d);
	if (dd > 1) {
		dd--;  // one day back
	} else {
		dm--;  // one month back
		if (dm <= 0) {
			dy--;
			dm = 12;
		}
		dd = lastDayOfMonth(dy, dm);
	}
	return joinSimpleDate(dy, dm, dd);
}

// Compute number of months in difference to-from (ignore days)
export function monthsDiff(from: SimpleDate, to: SimpleDate) {
	const [fy, fm] = splitSimpleDate(from);
	const [ty, tm] = splitSimpleDate(to);
	const md = tm - fm;
	const yd = ty - fy;
	return md + yd * 12;
}

// Return the beginning of the trimester d is in
export function trimesterStart(d: SimpleDate): SimpleDate {  // first day of trimester d falls in
	const [dy, dm] = splitSimpleDate(d);
	const m = Math.floor((dm - 1) / 3) * 3 + 1;  // value in [1..12]
	return `${dy}-${m < 10 ? '0' : ''}${m}-01` as SimpleDate;
}
